import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import Input from '../../shared/Input/Input';
import cn from 'classnames';
import styles from './MultipleFileUpload.styles.scss';
import { getFileDownloadUrl } from '../../../services/apis';
const fileIcon = require('../../../assets/png/tabler_file-description.png');
const MultipleFileUpload = ({
  field,
  form,
  placeholder,
  maximumFiles,
  acceptedFileTypes,
  setTouched,
  setFieldValue,
  disabled,
  readOnly,
  fileTypeErrorMessage,
  downloadFileLabel,
}) => {
  const { t } = useTranslation();

  const [files, setFiles] = useState(field.value || []);
  const [fileTypeError, setFileTypeError] = useState('');
  const fileInputRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [_allFilesDownloaded, setAllFilesDownloaded] = useState(false);

  const isFileAccepted = (file, acceptedTypes) => {
    if (!acceptedTypes) {
      return true;
    }
    const types = acceptedTypes.split(',').map((t) => t.trim());
    return types.some((type) => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      } else if (type.endsWith('/*')) {
        return file.type.startsWith(type.slice(0, -1));
      } else {
        return file.type === type;
      }
    });
  };

  const handleAddFile = (e) => {
    setTouched({ ...form.touched, [field.name]: true });
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const rejectedFiles = newFiles.filter(
        (f) => !isFileAccepted(f, acceptedFileTypes),
      );
      setFileTypeError('');
      if (rejectedFiles.length > 0) {
        setFileTypeError(
          fileTypeErrorMessage ??
            t('invalid_file_type', {
              files: rejectedFiles.map((f) => f.name).join(', '),
              accepted: acceptedFileTypes,
            }),
        );
        e.target.value = '';
        return;
      }

      const filteredNewFiles = newFiles.filter(
        (newFile) =>
          !files.some(
            (existingFile) =>
              existingFile.name === newFile.name &&
              existingFile.size === newFile.size,
          ),
      );
      if (filteredNewFiles.length < newFiles.length) {
        setFileTypeError(t('duplicate_file_error'));
      }
      const updatedFiles = files.concat(filteredNewFiles);

      setFiles(updatedFiles);
      setFieldValue(field.name, updatedFiles);
    }
  };

  const handleDelete = (e) => {
    const updatedFiles = files.filter((file) => file.name !== e.name);
    setFiles(updatedFiles);
    form.setFieldValue(field.name, updatedFiles);
  };

  const handleDownload = async (file, index) => {
    //api call to get the link
    const res = await getFileDownloadUrl(file.url);
    const link = document.createElement('a');
    link.href = res.data.filePath;
    link.target = '_blank';
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles];
      updatedFiles[index] = { ...updatedFiles[index], downloaded: true };

      if (
        updatedFiles.filter((_file) => _file.downloaded).length ===
        updatedFiles.length
      ) {
        setAllFilesDownloaded(true);
        setFieldValue('allFilesDownloaded', true);
      } else {
        setAllFilesDownloaded(false);
        setFieldValue('allFilesDownloaded', false);
      }

      return updatedFiles;
    });
  };

  const handleInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    setFiles(field.value || []);
  }, [field.value]);

  return (
    <>
      <input
        className="d-none"
        name={field.name}
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleAddFile}
        onClick={(e) => {
          setTouched({ ...form.touched, [field.name]: true });
          form.validateField(field.name);
          e.target.value = null;
        }}
        accept={acceptedFileTypes}
      />
      {fileTypeError && (
        <small className="p-error d-block mb-1">{fileTypeError}</small>
      )}
      {files.length === 0 && !readOnly && (
        <div className="w-100">
          <Input
            className={styles.uploadInput}
            value={files.map((file) => file.name).join(', ')}
            placeholder={placeholder}
            readOnly
            onClick={handleInputClick}
            startAdornment={
              <i
                className={cn('pi pi-upload', { 'p-disabled': disabled })}
                onClick={handleInputClick}
                style={{ cursor: 'pointer' }}
              />
            }
            disabled={disabled}
          />
        </div>
      )}
      {files.length > 0 &&
        files.map((file, index) => (
          <div
            className="p-inputgroup"
            key={`${file.name}-${index}`}
            style={{ marginBottom: '0.5rem' }}
          >
            <Input
              className={styles.uploadedFileInput}
              endAdornment={
                <div className={cn(styles.changeFileIcon)}>
                  <img src={fileIcon} alt="file icon" />
                </div>
              }
              startAdornment={
                file.uploading ? (
                  <i className="pi pi-spin pi-spinner" />
                ) : (
                  <Button
                    type="button"
                    onClick={
                      readOnly
                        ? () => handleDownload(file, index)
                        : () => handleDelete(file)
                    }
                    link
                    className={cn(
                      'p-0 color-theme-primary',
                      styles.fileActionButton,
                    )}
                  >
                    <i
                      className={cn(
                        styles.fileIcon,
                        'color-theme-primary',
                        readOnly ? 'pi pi-download' : 'pi icn-trash',
                      )}
                      title={readOnly ? 'Download file' : 'Delete file'}
                    />
                    {readOnly ? downloadFileLabel : ''}
                    {file.uploadTried && (
                      <i
                        className={cn(
                          styles.fileIcon,
                          file.successfulUpload ? 'pi pi-check' : 'pi pi-times',
                        )}
                      />
                    )}
                  </Button>
                )
              }
              value={file.name}
              disabled={readOnly}
              readOnly
            />
          </div>
        ))}
      {files.length > 0 && files.length < maximumFiles && !readOnly && (
        <Button
          type="button"
          className={cn(styles.addMoreButton, 'd-inline-flex gap-2 p-0')}
          onClick={handleInputClick}
          link
        >
          <i className="icn-plus-circle" />
          <span>
            {t('Add more files')} &nbsp;
            <span className={styles.remainingFiles}>
              {maximumFiles - files.length === 1
                ? t('remaining_file', {
                    remaining: maximumFiles - files.length,
                  })
                : t('remaining_files', {
                    remaining: maximumFiles - files.length,
                  })}
            </span>
          </span>
        </Button>
      )}
    </>
  );
};

export default MultipleFileUpload;
